/**
 * GetMaiBasicR2_CompanyDetails.Status 기준 (서버 CASE 순서와 동일)
 * 활용률 = 최근 30일 PROC_ACTION_LOG 고유 USER_ID ÷ TB_BS_COMPANY.CONTRACT_MEMBER_COUNT
 */
import { Box, Typography, Chip } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

export const CONTRACT_STATUS_LEGEND = [
  {
    status: '위험',
    summary: '만료 임박 + 활용률 20% 미만',
    description:
      '계약 종료일까지 30일 미만이고, 최근 30일 활성 사용자 비율이 계약 인원의 20% 미만일 때 표시됩니다.',
    chipSx: {
      bgcolor: 'rgba(239,68,68,0.1)',
      color: '#ef4444',
      border: '1px solid rgba(239,68,68,0.2)',
    },
  },
  {
    status: '주의',
    summary: '활용률 30% 미만',
    description:
      '계약 인원이 있고, 최근 30일 활용률이 30% 미만이며, 위 「위험」 조건(만료 30일 이내 + 20% 미만)에는 해당하지 않을 때 표시됩니다.',
    chipSx: {
      bgcolor: 'rgba(251,191,36,0.1)',
      color: '#f59e0b',
      border: '1px solid rgba(251,191,36,0.2)',
    },
  },
  {
    status: '종료',
    summary: '계약 종료',
    description:
      '계약 종료일이 지난 회사입니다. 목록에서는 유효 계약 아래쪽에 정렬됩니다.',
    chipSx: {
      bgcolor: 'rgba(100,116,139,0.12)',
      color: '#64748b',
      border: '1px solid rgba(100,116,139,0.25)',
    },
  },
  {
    status: '양호',
    summary: '그 외 유효 계약',
    description:
      '계약이 유효하고, 위 조건(위험·주의·종료)에 해당하지 않는 경우입니다.',
    chipSx: {
      bgcolor: 'rgba(16,185,129,0.1)',
      color: '#10b981',
      border: '1px solid rgba(16,185,129,0.2)',
    },
  },
];

export const USAGE_RATE_FORMULA_NOTE =
  '활용률(%) = 최근 30일 활성 사용자 수 ÷ 계약 인원 × 100 · 활성 사용자는 PROC_ACTION_LOG(화면 저장) 기준 고유 사용자입니다.';

/**
 * GetMaiBasicR3_CompanyInfo.ComplianceScore 산식 (MaiBasicQueries.xml)
 * ROUND( 참여점수 + 활동점수, 0 ) — 각 항목 최대 50점, 합계 최대 100점
 */
export function ComplianceScoreTooltipContent() {
  return (
    <Box sx={{ maxWidth: 340, p: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
        준수 점수 (0~100점)
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mb: 1, lineHeight: 1.5 }}>
        최근 30일 <strong>PROC_ACTION_LOG</strong>(관리자 웹 저장·수정·삭제) 기준이며,
        반올림한 정수(%)로 표시합니다.
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.25 }}>
        ① 참여 점수 (최대 50점)
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mb: 0.75, lineHeight: 1.5 }}>
        (활성 사용자 수 ÷ 계약 인원) × 50 → 50점 상한
        <br />
        · 활성 사용자 = 기간 내 로그가 있는 고유 USER_ID
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.25 }}>
        ② 활동 점수 (최대 50점)
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', mb: 0.75, lineHeight: 1.5 }}>
        (총 로그 건수 ÷ 활성 사용자 수) × 2 → 50점 상한
        <br />
        · 1인당 평균 활동(로그)이 많을수록 가산
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.5 }}>
        <strong>합계</strong> = ① + ② (활성 사용자가 없으면 활동 점수는 산출되지 않음)
      </Typography>
    </Box>
  );
}

export const getContractStatusChipSx = (status) => {
  const found = CONTRACT_STATUS_LEGEND.find((item) => item.status === status);
  return (
    found?.chipSx ?? {
      bgcolor: 'rgba(148,163,184,0.15)',
      color: '#64748b',
      border: '1px solid rgba(148,163,184,0.3)',
    }
  );
};

export function ContractStatusLegendPanel() {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid rgba(231,237,243,1)',
        bgcolor: 'rgba(19,127,236,0.03)',
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
        <InfoOutlined
          sx={{ fontSize: 20, color: '#137fec', mt: 0.15, flexShrink: 0 }}
        />
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 900 }}>
            상태(Status) 안내
          </Typography>
          <Typography
            sx={{
              mt: 0.35,
              fontSize: 12,
              color: 'text.secondary',
              fontWeight: 600,
              lineHeight: 1.55,
            }}
          >
            {USAGE_RATE_FORMULA_NOTE}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            lg: 'repeat(4, 1fr)',
          },
          gap: 1.25,
        }}
      >
        {CONTRACT_STATUS_LEGEND.map((item) => (
          <Box
            key={item.status}
            sx={{
              p: 1.25,
              borderRadius: 1.5,
              bgcolor: 'background.paper',
              border: '1px solid rgba(231,237,243,0.95)',
            }}
          >
            <Chip
              label={item.status}
              size="small"
              sx={{
                height: 24,
                fontSize: 12,
                fontWeight: 800,
                mb: 0.75,
                ...item.chipSx,
              }}
            />
            <Typography sx={{ fontSize: 12, fontWeight: 800, lineHeight: 1.35 }}>
              {item.summary}
            </Typography>
            <Typography
              sx={{
                mt: 0.35,
                fontSize: 11,
                color: 'text.secondary',
                fontWeight: 600,
                lineHeight: 1.5,
              }}
            >
              {item.description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
